/**
 * Entity Table Component
 * Reusable table with sticky headers, expandable cells, and artifact details
 */

import React from 'react';
import { ArrowUp, ArrowDown, FileText } from 'lucide-react';
import { renderFieldValue, renderExpandedValue, detectFieldType } from '../../utils/entityFieldFormatter';
import { getArtifact } from '../../api/artifacts';

export interface EntityDetail {
  id: string;
  data: any;
  source_artifact_id?: string;
}

interface EntityTableProps {
  entities: EntityDetail[];
  fields: string[];
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  filters: Record<string, any>;
  expandedCells: Set<string>;
  expandedArtifacts: Set<string>;
  artifactDetails: Record<string, any>;
  loadingArtifacts: Set<string>;
  onSort: (column: string) => void;
  onToggleExpanded: (entityId: string, column: string) => void;
  onToggleArtifact: (artifactId: string) => Promise<void>;
}

export function EntityTable({
  entities,
  fields,
  sortColumn,
  sortDirection,
  filters,
  expandedCells,
  expandedArtifacts,
  artifactDetails,
  loadingArtifacts,
  onSort,
  onToggleExpanded,
  onToggleArtifact,
}: EntityTableProps) {
  return (
    <div className="flex-1 overflow-auto border border-theme-border rounded-lg">
      <table className="min-w-full divide-y divide-theme-border">
        <thead className="bg-theme-elevated sticky top-0 z-20">
          <tr>
            <th className="px-2 py-2 text-left text-xs font-medium text-theme-text-secondary uppercase tracking-wider sticky left-0 bg-theme-elevated z-30 border-r border-theme-border w-[180px]">
              Entity ID
            </th>
            <th className="px-2 py-2 text-left text-xs font-medium text-theme-text-secondary uppercase tracking-wider bg-theme-elevated z-20 w-[180px]">
              Source Artifact ID
            </th>
            {fields.map((key) => {
              const sampleValue = entities.find(e => e.data?.[key] !== null && e.data?.[key] !== undefined)?.data?.[key];
              const fieldMeta = sampleValue !== undefined ? detectFieldType(key, sampleValue) : null;
              const isSorted = sortColumn === key;
              
              return (
                <th
                  key={key}
                  className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary uppercase tracking-wider cursor-pointer hover:bg-theme-surface-hover select-none"
                  onClick={() => onSort(key)}
                >
                  <div className="flex items-center gap-1">
                    <span>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    {isSorted && (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    )}
                    {filters[key] && (
                      <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full" title="Filtered" />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bg-theme-paper divide-y divide-theme-border">
          {entities.map((entity) => (
            <tr
              key={entity.id}
              className="hover:bg-theme-surface-hover"
            >
              <td className="px-2 py-2 text-sm font-mono text-theme-text sticky left-0 bg-theme-paper z-10 border-r border-theme-border w-[180px] break-words">
                <span className="select-all">{entity.id}</span>
              </td>
              <td className="px-2 py-2 text-sm bg-theme-paper w-[180px]">
                {entity.source_artifact_id ? (
                  <div className="space-y-1">
                    <button
                      onClick={async () => {
                        await onToggleArtifact(entity.source_artifact_id!);
                      }}
                      className="text-left w-full"
                    >
                      <div className="font-mono text-theme-text hover:text-primary-600 cursor-pointer select-all break-words">
                        {entity.source_artifact_id}
                      </div>
                    </button>
                    
                    {/* Expanded Artifact Details */}
                    {expandedArtifacts.has(entity.source_artifact_id) && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs space-y-1">
                        {loadingArtifacts.has(entity.source_artifact_id) ? (
                          <div className="text-blue-600">Loading...</div>
                        ) : artifactDetails[entity.source_artifact_id]?.error ? (
                          <div className="text-red-600">{artifactDetails[entity.source_artifact_id].error}</div>
                        ) : artifactDetails[entity.source_artifact_id] ? (
                          <>
                            <div className="font-semibold text-blue-900 flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {artifactDetails[entity.source_artifact_id].filename || 'Unknown'}
                            </div>
                            <div className="text-blue-700">
                              <div><span className="font-medium">Type:</span> {artifactDetails[entity.source_artifact_id].artifact_type || 'N/A'}</div>
                              <div><span className="font-medium">Size:</span> {artifactDetails[entity.source_artifact_id].file_size_bytes 
                                ? ((artifactDetails[entity.source_artifact_id].file_size_bytes / 1024 / 1024).toFixed(2) + ' MB')
                                : 'N/A'}</div>
                              <div><span className="font-medium">Date:</span> {artifactDetails[entity.source_artifact_id].created_at 
                                ? new Date(artifactDetails[entity.source_artifact_id].created_at).toLocaleDateString()
                                : 'N/A'}</div>
                              {artifactDetails[entity.source_artifact_id].source_type && (
                                <div><span className="font-medium">Source:</span> {artifactDetails[entity.source_artifact_id].source_type}</div>
                              )}
                            </div>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-theme-text-secondary">-</span>
                )}
              </td>
              {fields.map((key) => {
                const value = entity.data[key];
                const cellKey = `${entity.id}-${key}`;
                const isExpanded = expandedCells.has(cellKey);
                const fieldMeta = detectFieldType(key, value);
                
                return (
                  <td
                    key={key}
                    className="px-4 py-3 text-sm max-w-xs"
                    onDoubleClick={() => {
                      if (fieldMeta.isNested) {
                        onToggleExpanded(entity.id, key);
                      }
                    }}
                    title={fieldMeta.isNested ? 'Double-click to expand' : undefined}
                  >
                    <div className={fieldMeta.isNested ? 'cursor-pointer' : ''}>
                      {renderFieldValue(
                        key,
                        value,
                        isExpanded,
                        fieldMeta.isNested ? () => onToggleExpanded(entity.id, key) : undefined
                      )}
                      {isExpanded && fieldMeta.isNested && (
                        <div className="mt-2 border-t border-theme-border pt-2">
                          {renderExpandedValue(key, value)}
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}











